import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsService, PlatformStats, RevenueByPlan, ActivityTrend, TopGroup, AuditLog, LoginCounts } from '../../services/analyticsService';
import { Users, TrendingUp, DollarSign, AlertCircle, Activity, Shield } from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, color }) => (
  <div className="bg-card border border-border rounded-md p-4">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-xs text-muted-foreground">{title}</h3>
      <div className="p-2 rounded-md" style={{ backgroundColor: `${color}20` }}>
        {icon}
      </div>
    </div>
    <div className="flex items-end justify-between gap-3">
      <p className="text-2xl font-bold text-foreground">{value}</p>
      {trend && <span className="text-xs text-success font-medium">{trend}</span>}
    </div>
  </div>
);

const PlatformAnalytics: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loginCounts, setLoginCounts] = useState<LoginCounts | null>(null);
  const [revenueByPlan, setRevenueByPlan] = useState<RevenueByPlan[]>([]);
  const [activityTrends, setActivityTrends] = useState<ActivityTrend[]>([]);
  const [topGroups, setTopGroups] = useState<TopGroup[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [timeRange, setTimeRange] = useState<'7' | '30' | '90'>('30');
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = userProfile?.role === 'super_admin';

  useEffect(() => {
    let isMounted = true;

    const loadAnalyticsData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [statsData, loginCountsData, revenueData, trendsData, groupsData, logsData] = await Promise.all([
          analyticsService.getPlatformStats(),
          analyticsService.getLoginCounts(),
          analyticsService.getRevenueByPlan(),
          analyticsService.getActivityTrends(Number(timeRange)),
          analyticsService.getTopGroups(10),
          analyticsService.getAuditLogs(50)
        ]);

        if (isMounted) {
          setStats(statsData);
          setLoginCounts(loginCountsData);
          setRevenueByPlan(revenueData);
          setActivityTrends(trendsData);
          setTopGroups(groupsData);
          setAuditLogs(logsData);
        }
      } catch (error) {
        console.error('Error loading analytics:', error);
        if (isMounted) {
          setError(error instanceof Error ? error.message : 'Failed to load analytics');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (isSuperAdmin) {
      loadAnalyticsData();
    }

    return () => {
      isMounted = false;
    };
  }, [timeRange, isSuperAdmin]);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} VT`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} isMobileMenuOpen={isMobileMenuOpen} />

      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar isCollapsed={isSidebarCollapsed} userRole="admin" isSuperAdmin={true} />
        </div>

        <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-4 lg:p-6 max-w-7xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">Platform Analytics</h1>
                <p className="text-sm text-muted-foreground">
                  System-wide business intelligence and performance metrics (super admin).
                </p>
              </div>

              <div className="flex gap-2">
                {(['7', '30', '90'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      timeRange === range
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {range} days
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading analytics...</div>
              </div>
            ) : (

              <>
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <MetricCard
                    title="Total Users"
                    value={stats?.totalUsers || 0}
                    icon={<Users className="w-5 h-5 text-blue-600" />}
                    color="#3B82F6"
                  />
                  <MetricCard
                    title="Active Groups"
                    value={stats?.activeGroups || 0}
                    icon={<TrendingUp className="w-5 h-5 text-green-600" />}
                    color="#10B981"
                  />
                  <MetricCard
                    title="Total Revenue"
                    value={formatCurrency(stats?.totalRevenue || 0)}
                    icon={<DollarSign className="w-5 h-5 text-yellow-600" />}
                    color="#F59E0B"
                  />
                  <MetricCard
                    title="Pending Payments"
                    value={stats?.pendingPayments || 0}
                    icon={<AlertCircle className="w-5 h-5 text-red-600" />}
                    color="#EF4444"
                  />
                  <MetricCard
                    title="Logins Today"
                    value={loginCounts?.todayUniqueUsers || 0}
                    icon={<Icon name="LogIn" size={20} className="text-indigo-600" />}
                    color="#6366F1"
                    trend={loginCounts ? `${loginCounts.todayEvents} events` : undefined}
                  />
                  <MetricCard
                    title="Logins This Week"
                    value={loginCounts?.weekUniqueUsers || 0}
                    icon={<Icon name="LogIn" size={20} className="text-indigo-600" />}
                    color="#6366F1"
                    trend={loginCounts ? `${loginCounts.weekEvents} events` : undefined}
                  />
                  <MetricCard
                    title="Total Contributions"
                    value={stats?.totalContributions || 0}
                    icon={<Activity className="w-5 h-5 text-purple-600" />}
                    color="#8B5CF6"
                  />
                  <MetricCard
                    title="Active Subscriptions"
                    value={stats?.activeSubscriptions || 0}
                    icon={<Shield className="w-5 h-5 text-indigo-600" />}
                    color="#6366F1"
                  />
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  {/* Activity Trends Line Chart */}
                  <div className="bg-card border border-border rounded-md p-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Activity Trends</h2>
                    {activityTrends.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No data available for this range.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={activityTrends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={formatDate} />
                          <YAxis />
                          <Tooltip labelFormatter={formatDate} />
                          <Legend />
                          <Line type="monotone" dataKey="contributions" stroke="#3B82F6" name="Contributions" />
                          <Line type="monotone" dataKey="newUsers" stroke="#10B981" name="New Users" />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {/* Revenue by Plan Pie Chart */}
                  <div className="bg-card border border-border rounded-md p-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Revenue by Plan</h2>
                    {revenueByPlan.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No revenue data available.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={revenueByPlan}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry: any) => `${entry.planName}: ${formatCurrency(entry.revenue)}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="revenue"
                          >
                            {revenueByPlan.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Top Groups Bar Chart */}
                <div className="bg-card border border-border rounded-md p-4 mb-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Top Active Groups</h2>
                  {topGroups.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No group activity yet.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={380}>
                      <BarChart data={topGroups.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={-15} height={60} textAnchor="end" />
                        <YAxis />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="contributionAmount" fill="#3B82F6" name="Total Contributions (VT)" />
                        <Bar dataKey="totalContributions" fill="#10B981" name="Number of Contributions" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Audit Logs Table */}
                <div className="bg-card border border-border rounded-md p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground">Recent Activity Logs</h2>
                    <span className="text-xs text-muted-foreground">Last 20 events</span>
                  </div>
                  {auditLogs.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No audit logs found.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">User</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Action</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timestamp</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {auditLogs.slice(0, 20).map((log) => (
                            <tr key={log.id} className="hover:bg-muted/30">
                              <td className="px-4 py-3 text-sm text-foreground">{log.userName || 'System'}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">
                                  {log.actionType.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">{log.actionDescription}</td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {new Date(log.createdAt).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PlatformAnalytics;